import React, { useEffect, useState  } from "react";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import axios from "axios";
import { useParams } from "react-router-dom";
import { ApiUrl } from "Services/Services";
import { toast } from "react-toastify";
import { MdDelete, MdAdd } from "react-icons/md";

// ...imports same as before
const EditFood = () => {
  const { id } = useParams();
  const [categories, setCategories] = useState([]);
  const [ingredientsList, setIngredientsList] = useState([]);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  const [formData, setFormData] = useState({
    name: "",
    price: "",
    categoryId: "",
    image: null,
    previewImage: "",
    ingredients: [], // holds { ingredientId, name, quantity, unit }
  });

  const [newIngredient, setNewIngredient] = useState({ ingredientId: "", quantity: "" });

  useEffect(() => {
    fetchFood();
    fetchCategories();
    fetchAllIngredients();
  }, []);

  const fetchFood = async () => {
    try {
      const res = await axios.get(`${ApiUrl}/food/${id}`);
      const foodData = res.data.food;

      // editor content
      const contentBlock = htmlToDraft(foodData.description || "");
      const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
      setEditorState(EditorState.createWithContent(contentState));

      setFormData((prev) => ({
        ...prev,
        name: foodData.name,
        price: foodData.price,
        categoryId: foodData.categoryId,
        previewImage: foodData.image,
        image: null,
        ingredients: foodData.ingredients.map((ing) => ({
          ingredientId: ing.id,
          name: ing.name,
          quantity: ing.FoodIngredient?.quantity || "",
          unit: ing.unit,
        })),
      }));
    } catch {
      toast.error("Failed to fetch food");
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${ApiUrl}/category`);
      setCategories(res.data.data);
    } catch {
      toast.error("Failed to fetch categories");
    }
  };

  const fetchAllIngredients = async () => {
    try {
      const res = await axios.get(`${ApiUrl}/ingredients`);
      setIngredientsList(res.data.data);
    } catch {
      toast.error("Failed to fetch ingredients list");
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image") {
      setFormData((prev) => ({
        ...prev,
        image: files[0],
        previewImage: URL.createObjectURL(files[0]),
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleEditorChange = (state) => {
    setEditorState(state);
  };

const handleIngredientAdd = () => {
  if (!newIngredient.ingredientId || !newIngredient.quantity) {
    toast.error("Select ingredient and enter quantity");
    return;
  }
  const ingredientIdNum = Number(newIngredient.ingredientId); // Force number
  const ingredientObj = ingredientsList.find(
    (ing) => Number(ing.id) === ingredientIdNum
  );
  if (!ingredientObj) {
    toast.error("Ingredient not found");
    return;
  }

  // Prevent duplicates
  if (formData.ingredients.some((ing) => ing.ingredientId === ingredientIdNum)) {
    toast.error("Ingredient already added");
    return;
  }

  setFormData((prev) => ({
    ...prev,
    ingredients: [
      ...prev.ingredients,
      {
        ingredientId: ingredientObj.id,
        name: ingredientObj.name,
        unit: ingredientObj.unit,
        quantity: Number(newIngredient.quantity),
      },
    ],
  }));
  setNewIngredient({ ingredientId: "", quantity: "" });
};


  const handleIngredientRemove = (ingredientId) => {
    setFormData((prev) => ({
      ...prev,
      ingredients: prev.ingredients.filter((ing) => ing.ingredientId !== ingredientId),
    }));
  };

  const handleIngredientQuantityChange = (ingredientId, quantity) => {
    setFormData((prev) => ({
      ...prev,
      ingredients: prev.ingredients.map((ing) =>
        ing.ingredientId === ingredientId ? { ...ing, quantity } : ing
      ),
    }));
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  const htmlDescription = draftToHtml(convertToRaw(editorState.getCurrentContent()));

  const updateFormData = new FormData();
  updateFormData.append("name", formData.name);
  updateFormData.append("price", formData.price);
  updateFormData.append("categoryId", formData.categoryId);
  updateFormData.append("description", htmlDescription);
  updateFormData.append("ingredients", JSON.stringify(formData.ingredients.map(ing => ({
    ingredientId: Number(ing.ingredientId),
    quantity: Number(ing.quantity)
  }))));

  if (formData.image) {
    updateFormData.append("image", formData.image);
  }

  try {
    await axios.put(`${ApiUrl}/food/${id}`, updateFormData);
    toast.success("Food updated successfully");
  } catch {
    toast.error("Update failed");
  }
};

  return !formData.name ? (
    <p>Loading...</p>
  ) : (
    <div className="max-w-4xl mx-auto mt-10 bg-white shadow-lg rounded-xl p-6 space-y-6">
      <h2 className="text-2xl font-bold mb-4">Edit Food</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Basic Info */}
        <div>
          <label className="block font-medium">Name</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded"
            required
          />
        </div>

        <div>
          <label className="block font-medium">Price</label>
          <input
            type="number"
            name="price"
            value={formData.price}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded"
            required
          />
        </div>

        <div>
          <label className="block font-medium">Category</label>
          <select
            name="categoryId"
            value={formData.categoryId}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded"
            required
          >
            <option value="">Select Category</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.title}
              </option>
            ))}
          </select>
        </div>

        {/* Description */}
        <div>
          <label className="block font-medium mb-1">Description</label>
          <Editor
            editorState={editorState}
            wrapperClassName="border rounded bg-white"
            editorClassName="px-3 py-2 min-h-[150px]"
            onEditorStateChange={handleEditorChange}
          />
        </div>

        {/* Image */}
        <div>
          <label className="block font-medium">Image</label>
          <input type="file" name="image" accept="image/*" onChange={handleChange} />
          {formData.previewImage && (
            <img
              src={formData.previewImage}
              alt="Preview"
              className="mt-2 h-32 w-32 object-cover rounded border"
            />
          )}
        </div>

        {/* Ingredients Section */}
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-2">Ingredients</h3>
          {formData.ingredients.length > 0 ? (
            <ul className="space-y-2">
              {formData.ingredients.map((ing) => (
                <li
                  key={ing.ingredientId}
                  className="flex justify-between items-center border p-2 rounded"
                >
                  <span className="flex-1">
                    {ing.name} —{" "}
                    <input
                      type="number"
                      value={ing.quantity}
                      onChange={(e) =>
                        handleIngredientQuantityChange(ing.ingredientId, e.target.value)
                      }
                      className="border rounded px-2 py-1 w-20"
                    />{" "}
                    {ing.unit}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleIngredientRemove(ing.ingredientId)}
                    className="text-red-500 hover:text-red-700 ml-2"
                  >
                    <MdDelete size={20} />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500">No ingredients linked.</p>
          )}

          {/* Add New Ingredient */}
          <div className="flex gap-2 mt-4">
            <select
              value={newIngredient.ingredientId}
              onChange={(e) =>
                setNewIngredient({ ...newIngredient, ingredientId: e.target.value })
              }
              className="border rounded px-2 py-1"
            >
              <option value="">Select Ingredient</option>
              {ingredientsList.map((ing) => (
                <option key={ing.id} value={ing.id}>
                  {ing.name} ({ing.unit})
                </option>
              ))}
            </select>
            <input
              type="number"
              placeholder="Quantity"
              value={newIngredient.quantity}
              onChange={(e) =>
                setNewIngredient({ ...newIngredient, quantity: e.target.value })
              }
              className="border rounded px-2 py-1 w-24"
            />
            <button
              type="button"
              onClick={handleIngredientAdd}
              className="bg-green-500 text-white px-3 rounded hover:bg-green-600 flex items-center gap-1"
            >
              <MdAdd /> Add
            </button>
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Update Food
        </button>
      </form>
    </div>
  );
};

export default EditFood;
